import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download, Eye, FileText, Image as ImageIcon, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { apiGet } from '@/api/axios-client'
import { ImageLightbox } from '@/components/composites/image-lightbox'
import { canViewPdfInApp, isPdf, PdfViewer, type PdfBytes } from '@/components/composites/pdf-viewer'

/**
 * The files on an item, and how they get opened.
 *
 * A stored key is not a URL and cannot be fetched: the bucket is private, which is the whole
 * point — a public link to a file on a restricted work item is that item's contents readable
 * by anyone who has ever seen the link. So opening one asks the API for a signed URL, and the
 * API mints it only after the caller passes the same visibility check the board applies.
 *
 * Images stay inside Tapwork: their authorized signed URL is used for an inline thumbnail and
 * an in-app lightbox. PDFs open in the in-app viewer. Other file types keep the new-tab behaviour.
 */

/** Structural on purpose — a composite may not import a domain entity. */
export interface AttachmentView {
  id: string
  name: string
  contentType: string
  size: number
}

const prettySize = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`

function ImageAttachment({
  kind,
  itemId,
  attachment,
}: {
  kind: 'card' | 'project' | 'finance' | 'review'
  itemId: string
  attachment: AttachmentView
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const urlQuery = useQuery({
    queryKey: ['attachment-url', kind, itemId, attachment.id],
    queryFn: () => apiGet<{ url: string }>(`/uploads/${kind}/${itemId}/${attachment.id}`),
    staleTime: 10 * 60_000,
    retry: 1,
  })

  async function showImage() {
    if (urlQuery.data?.url) {
      setLightboxOpen(true)
      return
    }
    const result = await urlQuery.refetch()
    if (result.data?.url) setLightboxOpen(true)
    else toast.error(`Could not open ${attachment.name}.`)
  }

  return (
    <>
      <li>
        <button
          type="button"
          onClick={() => void showImage()}
          disabled={urlQuery.isFetching && !urlQuery.data?.url}
          className="group block w-full overflow-hidden rounded-md border border-border bg-muted/20 text-left transition-colors dur-fast hover:border-brand/40 disabled:opacity-60"
          title={`View ${attachment.name}`}
        >
          <div className="flex min-h-28 items-center justify-center bg-surface-sunken">
            {urlQuery.data?.url ? (
              <img
                src={urlQuery.data.url}
                alt={attachment.name}
                loading="lazy"
                className="max-h-52 w-full object-contain"
              />
            ) : urlQuery.isError ? (
              <div className="flex items-center gap-2 px-3 py-6 text-xs text-danger">
                <ImageIcon className="size-4" />
                Preview unavailable — click to retry
              </div>
            ) : (
              <Loader2 className="size-5 animate-spin text-text-muted" />
            )}
          </div>
          <div className="flex items-center gap-2 px-3 py-2 text-xs">
            <ImageIcon className="size-4 shrink-0 text-text-muted" />
            <span className="min-w-0 flex-1 truncate font-medium" title={attachment.name}>
              {attachment.name}
            </span>
            <span className="shrink-0 text-text-muted">{prettySize(attachment.size)}</span>
          </div>
        </button>
      </li>
      <ImageLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        src={urlQuery.data?.url ?? null}
        alt={attachment.name}
      />
    </>
  )
}

function FileAttachment({
  kind,
  itemId,
  attachment,
}: {
  kind: 'card' | 'project' | 'finance' | 'review'
  itemId: string
  attachment: AttachmentView
}) {
  const [opening, setOpening] = useState(false)

  async function open() {
    setOpening(true)
    /*
     * The tab is opened synchronously, inside the click, and pointed at the URL once it
     * arrives. Opening it after the await instead would be a popup the browser blocks —
     * the gesture that permitted it is over by then.
     */
    const tab = window.open('', '_blank', 'noopener,noreferrer')
    try {
      const { url } = await apiGet<{ url: string }>(`/uploads/${kind}/${itemId}/${attachment.id}`)
      if (tab) tab.location.assign(url)
      else window.location.assign(url)
    } catch {
      tab?.close()
      toast.error(`Could not open ${attachment.name}.`)
    } finally {
      setOpening(false)
    }
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => void open()}
        disabled={opening}
        className="flex w-full items-center gap-2 rounded-md border border-border px-2 py-1.5 text-left transition-colors dur-fast hover:bg-muted disabled:opacity-60"
      >
        {opening ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-text-muted" />
        ) : (
          <FileText className="size-3.5 shrink-0 text-text-muted" />
        )}
        <span className="min-w-0 flex-1 truncate text-sm" title={attachment.name}>
          {attachment.name}
        </span>
        <span className="shrink-0 text-tiny text-text-muted">{prettySize(attachment.size)}</span>
        <Download className="size-3.5 shrink-0 text-text-muted" />
      </button>
    </li>
  )
}

function PdfAttachment({
  kind,
  itemId,
  attachment,
}: {
  kind: 'card' | 'project' | 'finance' | 'review'
  itemId: string
  attachment: AttachmentView
}) {
  const [open, setOpen] = useState(false)
  const getUrl = async (view: 'inline' | 'download') =>
    (await apiGet<{ url: string }>(`/uploads/${kind}/${itemId}/${attachment.id}`, { view })).url

  const view = () => {
    if (canViewPdfInApp()) return setOpen(true)
    // No in-page PDF engine (some phones): a tab is the best viewer there.
    const tab = window.open('', '_blank', 'noopener,noreferrer')
    getUrl('inline')
      .then((url) => (tab ? tab.location.assign(url) : window.location.assign(url)))
      .catch(() => {
        tab?.close()
        toast.error(`Could not open ${attachment.name}.`)
      })
  }

  return (
    <li>
      <div className="flex w-full items-center gap-1 rounded-md border border-border pr-1 transition-colors dur-fast hover:border-brand/40">
        <button type="button" onClick={view} className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left" title={`View ${attachment.name}`}>
          <span className="grid size-6 shrink-0 place-items-center rounded bg-danger/10 text-[9px] font-bold text-danger">PDF</span>
          <span className="min-w-0 flex-1 truncate text-sm" title={attachment.name}>{attachment.name}</span>
          <span className="shrink-0 text-tiny text-text-muted">{prettySize(attachment.size)}</span>
          <span className="flex shrink-0 items-center gap-1 rounded-md bg-brand-soft px-2 py-0.5 text-tiny font-semibold text-brand"><Eye className="size-3" /> View</span>
        </button>
        <button
          type="button"
          aria-label={`Download ${attachment.name}`}
          title="Download"
          onClick={() => {
            getUrl('download')
              .then((url) => window.location.assign(url))
              .catch(() => toast.error(`Could not download ${attachment.name}.`))
          }}
          className="grid size-7 shrink-0 place-items-center rounded-md text-text-muted hover:bg-muted hover:text-foreground"
        >
          <Download className="size-3.5" />
        </button>
      </div>
      <PdfViewer
        open={open}
        onOpenChange={setOpen}
        name={attachment.name}
        sizeLabel={prettySize(attachment.size)}
        getUrl={getUrl}
        getBytes={() => apiGet<PdfBytes>(`/uploads/${kind}/${itemId}/${attachment.id}/content`)}
      />
    </li>
  )
}

export function AttachmentList({
  kind,
  itemId,
  attachments,
  empty = 'None',
}: {
  kind: 'card' | 'project' | 'finance' | 'review'
  itemId: string
  attachments: AttachmentView[]
  empty?: string
}) {
  if (attachments.length === 0) {
    return <p className="text-sm text-text-muted">{empty}</p>
  }

  return (
    <ul className="grid gap-2">
      {attachments.map((attachment) =>
        attachment.contentType.startsWith('image/') ? (
          <ImageAttachment key={attachment.id} kind={kind} itemId={itemId} attachment={attachment} />
        ) : isPdf(attachment) ? (
          <PdfAttachment key={attachment.id} kind={kind} itemId={itemId} attachment={attachment} />
        ) : (
          <FileAttachment key={attachment.id} kind={kind} itemId={itemId} attachment={attachment} />
        ),
      )}
    </ul>
  )
}
